/* eslint-disable css-modules/no-unused-class */
import { FC } from 'react';
import classNames from 'classnames';
import { TableProgressBarProps } from './tableProgressBarTypes';
import styles from './tableProgressBar.module.scss';
import Tooltip from '../../tooltip/tooltip';
import { useTranslationContext } from '../../contexts/translationContext';

const TableProgressBar: FC<TableProgressBarProps> = (props: TableProgressBarProps) => {
    const { percent, completed, inProgress, notStarted, showTooltip } = props;
    const { translate } = useTranslationContext();

    let left = 0;
    let middle = 0;
    let right = 0;

    const notStartedText = translate('NotStarted');
    const inProgressText = translate('InProgress');
    const completedText = translate('Completed');

    if (percent !== undefined) {
        left = percent;
        right = 100 - percent;
    } else {
        left = completed || 0;
        middle = inProgress || 0;
        right = notStarted || 0;
    }
    const PercentageBox: FC<{ percentage: number; text: string; background: string }> = ({
        percentage,
        text,
        background
    }) => (
        <div className={styles['percentage-wrapper']} style={{ '--background': background } as any}>
            <div className={styles.box}></div>
            <div className={styles.percentage}> {Math.trunc(percentage)}%</div>
            <div className={styles.text}> {text}</div>
        </div>
    );

    const renderTooltip = () => {
        return (
            <div className={styles.tooltip}>
                <PercentageBox percentage={notStarted || 0} text={notStartedText} background="#E4DBFF" />
                <PercentageBox percentage={inProgress || 0} text={inProgressText} background="#D9CCFF" />
                <PercentageBox percentage={completed || 0} text={completedText} background="#C2ADEE" />
            </div>
        );
    };

    const getElement = () => {
        return (
            <div className={styles.box}>
                {left > 0 && (
                    <div
                        className={classNames(styles.left, {
                            [styles['left-border']]: left > 0,
                            [styles['right-border']]: left === 100,
                            [styles.borders]: right === 0 && middle === 0 && left === 100
                        })}
                        style={{ '--value': `${left}%` } as any}
                    ></div>
                )}
                {middle > 0 && (
                    <div
                        className={classNames(styles.middle, {
                            [styles['left-border']]: left === 0,
                            [styles['right-border']]: right === 0,
                            [styles.borders]: right === 0 && middle === 100 && left === 0
                        })}
                        style={{ '--value': `${middle}%` } as any}
                    ></div>
                )}
                {right > 0 && (
                    <div
                        className={classNames(styles.right, {
                            [styles['left-border']]: middle === 0,
                            [styles['right-border']]: right > 0,
                            [styles.borders]: right === 100 && middle === 0 && left === 0
                        })}
                        style={{ '--value': `${right}%` } as any}
                    ></div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.wrapper}>
            {showTooltip ? (
                <Tooltip title={renderTooltip()} type="secondary" disablePortal={false}>
                    {getElement()}
                </Tooltip>
            ) : (
                getElement()
            )}
        </div>
    );
};

export default TableProgressBar;
